<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import IconLogo from '@/components/icons/IconLogo.vue';
import { Icons } from '@/models/icons.enum';
import { AccountsService } from '@/services/accounts.service';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackstageBackLink from './components/BackstageBackLink.vue';
import CatalogDownloadSection from './components/CatalogDownload/CatalogDownloadSection.vue';
import ContactInfo from './components/ContactInfo.vue';
import HeroBanner from './components/HeroBanner.vue';
import ImportantDatesSection from './components/ImportantDates/ImportantDatesSection.vue';
import OrderGuidelinesSection from './components/OrderGuidelines/OrderGuidelinesSection.vue';
import SeasonNews from './components/SeasonNews/SeasonNews.vue';
import SeasonQuote from './components/SeasonQuote.vue';
import SectionHeader from './components/SectionHeader.vue';
import WelcomeVideo from './components/WelcomeVideo.vue';
import { useSelfSellIn } from './composables/useSelfSellIn';

const { sellIn, isLoading } = useSelfSellIn();
const { t } = useI18n();
const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());

const welcomeTitle = computed(() => sellIn.value?.welcomeAccountSection.title ?? '');
const welcomeDescription = computed(
  () => sellIn.value?.welcomeAccountSection.description.replace('{account}', selectedAccount.value?.name) ?? '',
);

const accountsService = new AccountsService();
const salesRepEmail = ref<string | null>(null);
onMounted(async () => {
  try {
    const result = await accountsService.getSalesRep(selectedAccountId.value);
    salesRepEmail.value = result.model.email;
  } catch {
    // silent fail — salesRepEmail stays null
  }
});
</script>

<template>
  <div class="sticky-logo-container">
    <IconLogo class="sticky-logo" />
  </div>
  <div v-if="isLoading" class="view-self-sell-in__loading">
    <BackstageIcon :name="Icons.CIRCLE_LOADING" class="view-self-sell-in__loading-icon" />
  </div>
  <div v-else-if="sellIn" class="view-self-sell-in">
    <HeroBanner :data="sellIn.heroBannerSection" />

    <SectionHeader :title="welcomeTitle" :description="welcomeDescription" />

    <SeasonQuote :data="sellIn.seasonQuoteSection" />

    <ImportantDatesSection v-if="sellIn.importantDatesSection" :data="sellIn.importantDatesSection" />

    <WelcomeVideo v-if="sellIn.welcomeVideoSection" :data="sellIn.welcomeVideoSection" />

    <SeasonNews v-if="sellIn.seasonNewsSection" :data="sellIn.seasonNewsSection" />

    <CatalogDownloadSection v-if="sellIn.catalogDownloadSection" :data="sellIn.catalogDownloadSection" />

    <OrderGuidelinesSection :data="sellIn.orderGuidelinesSection" />

    <ContactInfo authorName="" authorTitle="" authorAvatarUrl="" :authorEmail="salesRepEmail || ''" />

    <BackstageBackLink />
  </div>
  <div v-else class="view-self-sell-in__error">
    <p>{{ t('viewSelfSellIn.error_loading') }}</p>
  </div>
</template>

<style lang="scss" scoped>
:global(#backstage-ui:has(.view-self-sell-in)) {
  background-color: var(--primary-dark-color);
}

.view-self-sell-in__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

.view-self-sell-in__error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: var(--primary-dark-color);
}

.view-self-sell-in__loading-icon {
  width: 48px;
  height: 48px;
}

.view-self-sell-in {
  @include common-container;
  background-color: var(--primary-bg-color);
  position: relative;
  padding: 0 24px;

  display: flex;
  flex-direction: column;
  row-gap: 140px;
}

.sticky-logo-container {
  @include common-container;
  position: sticky;
  padding: 0 24px;
  top: 0;
  z-index: 100;
  mix-blend-mode: difference;
}

.sticky-logo {
  position: absolute;
  top: 34px;
  right: 24px;

  :deep(path) {
    fill: white;
  }
}
</style>
